import { useMemo, useState } from "react";
import { Ban, CheckCircle2, ShieldCheck, Search as SearchIcon, UserPlus, X, MessageCircleOff, ShieldAlert, AlertOctagon, MoreVertical } from "lucide-react";
import { getUsers, upsertUser } from "@/lib/storage";
import {
    ACCOUNT_STATUS,
    MESSAGING_STATUS,
    banUser,
    getEffectiveStatus,
    reinstateUser,
    restoreMessaging,
    suspendMessaging,
    suspendUser,
} from "@/lib/moderationStore";
import InitialAvatar from "@/components/InitialAvatar";
import axios from "axios";

const ROLE_BADGE = {
    admin: "bg-primary/15 text-primary border-primary/30",
    user:  "bg-muted text-muted-foreground border-border",
};

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_SECRET = process.env.REACT_APP_ADMIN_API_SECRET || "";

const AdminUsers = () => {
    const [q, setQ] = useState("");
    const [v, setV] = useState(0);

    // Add-user modal state
    const [showAdd, setShowAdd] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [creating, setCreating] = useState(false);
    const [createMsg, setCreateMsg] = useState(null);
    const [createOk, setCreateOk] = useState(false);

    // Moderation modal: { type: "suspend"|"ban"|"messaging", user }
    const [mod, setMod] = useState(null);

    const users = useMemo(() => {
        const all = getUsers();
        const t = q.trim().toLowerCase();
        if (!t) return all;
        return all.filter((u) =>
            [u.fullName, u.username, u.email, u.phone].some((f) => (f || "").toLowerCase().includes(t))
        );
    }, [q, v]);

    const togglePatch = (userId, patch) => {
        const all = getUsers();
        const u = all.find((x) => x.id === userId);
        if (!u) return;
        upsertUser({ ...u, ...patch });
        setV((x) => x + 1);
    };

    const submitAddUser = async (e) => {
        e?.preventDefault?.();
        if (creating) return;
        setCreateMsg(null);
        setCreateOk(false);
        const email = newEmail.trim().toLowerCase();
        const password = newPassword;
        if (!email || !email.includes("@")) {
            setCreateMsg("Enter a valid email.");
            return;
        }
        if (password.length < 8) {
            setCreateMsg("Password must be at least 8 characters.");
            return;
        }
        setCreating(true);
        try {
            const headers = { "Content-Type": "application/json" };
            if (ADMIN_SECRET) headers["X-Admin-Secret"] = ADMIN_SECRET;
            const { data } = await axios.post(
                `${API}/api/admin/create-user`,
                { email, password },
                { headers }
            );
            if (data?.success) {
                setCreateOk(true);
                setCreateMsg(
                    data.profile_warning
                        ? `User created. Profile note: ${data.profile_warning}`
                        : `User created successfully (${email}).`
                );
                setNewEmail("");
                setNewPassword("");
            } else {
                setCreateMsg("Unexpected response from server.");
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.message;
            // Sanitize Vercel-specific 405s and stream errors.
            let msg = typeof detail === "string" ? detail : "Could not create user. Check backend configuration.";
            if (/405|method not allowed|body stream|json/i.test(msg)) {
                msg = "Could not create user. Check backend configuration.";
            }
            setCreateMsg(msg);
        } finally {
            setCreating(false);
        }
    };

    const closeAdd = () => {
        setShowAdd(false);
        setCreateMsg(null);
        setCreateOk(false);
        setNewEmail("");
        setNewPassword("");
    };

    return (
        <div data-testid="admin-users" className="space-y-4">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Users</h2>
                <p className="text-[13px] text-muted-foreground">
                    View every registered user. Suspend, ban, promote to admin, or assign the verified badge.
                </p>
            </div>

            <div className="flex items-center gap-2">
                <label className="flex-1 max-w-md flex items-center gap-2 h-11 px-3.5 rounded-xl bg-secondary border border-border">
                    <SearchIcon size={14} className="text-muted-foreground" />
                    <input
                        data-testid="admin-users-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, username, email, phone"
                        className="flex-1 bg-transparent outline-none text-[13.5px]"
                    />
                </label>
                <button
                    type="button"
                    data-testid="admin-users-add"
                    onClick={() => setShowAdd(true)}
                    className="h-11 px-3.5 rounded-xl bg-primary text-primary-foreground hover:opacity-95 text-[12.5px] font-medium inline-flex items-center gap-1.5"
                >
                    <UserPlus size={14} />
                    Add user
                </button>
                <span className="text-[12px] text-muted-foreground" data-testid="admin-users-count">
                    {users.length} result{users.length === 1 ? "" : "s"}
                </span>
            </div>

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/50">
                        <tr>
                            <th className="px-3 py-2.5">User</th>
                            <th className="px-3 py-2.5 hidden md:table-cell">Contact</th>
                            <th className="px-3 py-2.5">Role</th>
                            <th className="px-3 py-2.5">Status</th>
                            <th className="px-3 py-2.5">Verified</th>
                            <th className="px-3 py-2.5 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {users.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground text-[13px]">No users.</td></tr>
                        )}
                        {users.map((u) => {
                            const role = u.role || "user";
                            const acctStatus = getEffectiveStatus(u);
                            const msgSuspended = (u.messaging_status || MESSAGING_STATUS.ACTIVE) === MESSAGING_STATUS.SUSPENDED;
                            const isBanned = acctStatus === ACCOUNT_STATUS.BANNED;
                            const isSuspended = acctStatus === ACCOUNT_STATUS.SUSPENDED;
                            const isActive = !isBanned && !isSuspended;
                            return (
                                <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <InitialAvatar name={u.fullName || u.username} size={32} />
                                            <div className="min-w-0">
                                                <div className="font-medium truncate">{u.fullName || "—"}</div>
                                                <div className="text-[11px] text-muted-foreground truncate">@{u.username}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground">
                                        <div>{u.email || "—"}</div>
                                        <div>{u.phone || ""}</div>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${ROLE_BADGE[role]}`}>
                                            {role}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex flex-col gap-1 items-start">
                                            <span
                                                data-testid={`admin-user-${u.id}-status`}
                                                className={[
                                                    "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border",
                                                    isBanned
                                                        ? "text-destructive border-destructive/30 bg-destructive/10"
                                                        : isSuspended
                                                        ? "text-amber-700 dark:text-amber-300 border-amber-500/30 bg-amber-500/10"
                                                        : "text-emerald-700 dark:text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
                                                ].join(" ")}
                                            >
                                                {isBanned ? (<><ShieldAlert size={11} /> Banned</>) : isSuspended ? (<><AlertOctagon size={11} /> Suspended</>) : (<><CheckCircle2 size={11} /> Active</>)}
                                            </span>
                                            {msgSuspended && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                                                    <MessageCircleOff size={10} />
                                                    Msg suspended
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        {u.verified ? <ShieldCheck size={14} className="text-primary" /> : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                            {isActive && (
                                                <button
                                                    data-testid={`admin-user-${u.id}-suspend`}
                                                    onClick={() => setMod({ type: "suspend", user: u })}
                                                    className="h-8 px-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[11.5px] font-medium inline-flex items-center gap-1"
                                                >
                                                    <AlertOctagon size={12} />
                                                    Suspend
                                                </button>
                                            )}
                                            {!isBanned && (
                                                <button
                                                    data-testid={`admin-user-${u.id}-ban`}
                                                    onClick={() => setMod({ type: "ban", user: u })}
                                                    className="h-8 px-2.5 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/15 text-[11.5px] font-medium inline-flex items-center gap-1"
                                                >
                                                    <Ban size={12} />
                                                    Ban
                                                </button>
                                            )}
                                            {!isActive && (
                                                <button
                                                    data-testid={`admin-user-${u.id}-reinstate`}
                                                    onClick={() => { reinstateUser(u.id); setV((x) => x + 1); }}
                                                    className="h-8 px-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[11.5px] font-medium inline-flex items-center gap-1"
                                                >
                                                    <CheckCircle2 size={12} />
                                                    Reinstate
                                                </button>
                                            )}
                                            {msgSuspended ? (
                                                <button
                                                    data-testid={`admin-user-${u.id}-restore-messaging`}
                                                    onClick={() => { restoreMessaging(u.id); setV((x) => x + 1); }}
                                                    className="h-8 px-2.5 rounded-lg border border-border bg-secondary hover:bg-muted text-[11.5px] font-medium inline-flex items-center gap-1"
                                                >
                                                    <MessageCircleOff size={12} />
                                                    Restore msg
                                                </button>
                                            ) : (
                                                <button
                                                    data-testid={`admin-user-${u.id}-suspend-messaging`}
                                                    onClick={() => setMod({ type: "messaging", user: u })}
                                                    className="h-8 px-2.5 rounded-lg border border-border bg-secondary hover:bg-muted text-[11.5px] font-medium inline-flex items-center gap-1"
                                                >
                                                    <MessageCircleOff size={12} />
                                                    Mute msg
                                                </button>
                                            )}
                                            <button
                                                data-testid={`admin-user-${u.id}-toggle-admin`}
                                                onClick={() => togglePatch(u.id, { role: role === "admin" ? "user" : "admin" })}
                                                className="h-8 px-2.5 rounded-lg border border-primary/40 bg-primary/10 text-primary hover:bg-primary/15 text-[11.5px] font-medium"
                                            >
                                                {role === "admin" ? "Revoke admin" : "Make admin"}
                                            </button>
                                            <button
                                                data-testid={`admin-user-${u.id}-toggle-verified`}
                                                onClick={() => togglePatch(u.id, { verified: !u.verified })}
                                                className="h-8 px-2.5 rounded-lg border border-border bg-secondary hover:bg-muted text-[11.5px] font-medium inline-flex items-center gap-1"
                                            >
                                                <CheckCircle2 size={12} />
                                                {u.verified ? "Unverify" : "Verify"}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showAdd && (
                <div
                    data-testid="admin-users-add-modal"
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={closeAdd}
                >
                    <div
                        className="w-full max-w-sm rounded-2xl bg-card border border-border shadow-card p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display text-[16px] font-semibold inline-flex items-center gap-2">
                                <UserPlus size={16} className="text-primary" />
                                Add user
                            </h3>
                            <button
                                data-testid="admin-users-add-close"
                                onClick={closeAdd}
                                className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground"
                                aria-label="Close"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <p className="text-[12px] text-muted-foreground mb-3">
                            Creates a new Supabase auth user (email auto-confirmed) and a matching profile record.
                        </p>

                        <form onSubmit={submitAddUser} className="space-y-3" autoComplete="off">
                            <label className="block">
                                <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">Email</span>
                                <input
                                    data-testid="admin-users-add-email"
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => { setNewEmail(e.target.value); if (createMsg) setCreateMsg(null); }}
                                    placeholder="user@example.com"
                                    autoComplete="off"
                                    className="w-full h-11 px-3.5 rounded-xl bg-secondary border border-border focus:border-primary focus:bg-background outline-none text-[13.5px]"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">Temporary password</span>
                                <input
                                    data-testid="admin-users-add-password"
                                    type="text"
                                    value={newPassword}
                                    onChange={(e) => { setNewPassword(e.target.value); if (createMsg) setCreateMsg(null); }}
                                    placeholder="At least 8 characters"
                                    autoComplete="new-password"
                                    className="w-full h-11 px-3.5 rounded-xl bg-secondary border border-border focus:border-primary focus:bg-background outline-none text-[13.5px] font-mono"
                                />
                            </label>

                            {createMsg && (
                                <p
                                    data-testid="admin-users-add-message"
                                    className={[
                                        "text-[12px] font-medium px-3 py-2 rounded-lg",
                                        createOk
                                            ? "text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/30"
                                            : "text-destructive bg-destructive/10 border border-destructive/30",
                                    ].join(" ")}
                                >
                                    {createMsg}
                                </p>
                            )}

                            <div className="flex items-center gap-2 pt-1">
                                <button
                                    type="button"
                                    data-testid="admin-users-add-cancel"
                                    onClick={closeAdd}
                                    className="flex-1 h-11 rounded-xl border border-border bg-secondary text-[13px] font-medium hover:bg-muted"
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    data-testid="admin-users-add-submit"
                                    disabled={creating}
                                    className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground text-[13px] font-medium disabled:opacity-60"
                                >
                                    {creating ? "Creating…" : "Create user"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {mod && (
                <ModerationModal
                    spec={mod}
                    onClose={() => setMod(null)}
                    onApplied={() => { setMod(null); setV((x) => x + 1); }}
                />
            )}
        </div>
    );
};

const ModerationModal = ({ spec, onClose, onApplied }) => {
    const { type, user } = spec;
    const [reason, setReason] = useState("");
    const [days, setDays] = useState("");
    const [error, setError] = useState(null);

    const apply = () => {
        const r = reason.trim();
        if (type === "ban" && !r) {
            setError("A reason is required to ban a user.");
            return;
        }
        if (type === "suspend" && !r) {
            setError("A reason is required to suspend a user.");
            return;
        }
        if (type === "messaging" && !r) {
            setError("A reason is required.");
            return;
        }
        if (type === "suspend") {
            const n = days === "" ? null : Number(days);
            if (days !== "" && (!Number.isFinite(n) || n <= 0)) {
                setError("Duration must be a positive number of days, or empty for indefinite.");
                return;
            }
            suspendUser(user.id, { reason: r, durationDays: n });
        } else if (type === "ban") {
            banUser(user.id, { reason: r });
        } else if (type === "messaging") {
            suspendMessaging(user.id, { reason: r });
        }
        onApplied();
    };

    const meta = type === "ban"
        ? { title: "Ban user", cta: "Ban", tone: "destructive" }
        : type === "suspend"
        ? { title: "Suspend user", cta: "Suspend", tone: "amber" }
        : { title: "Suspend messaging", cta: "Suspend messaging", tone: "amber" };

    return (
        <div
            data-testid="admin-users-mod-modal"
            className="fixed inset-0 z-[80] bg-black/55 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-sm rounded-2xl bg-card border border-border shadow-card p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display text-[16px] font-semibold">{meta.title}</h3>
                    <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground" aria-label="Close" data-testid="admin-users-mod-close">
                        <X size={16} />
                    </button>
                </div>
                <p className="text-[12px] text-muted-foreground mb-3">
                    {meta.title} <strong className="text-foreground">{user.fullName || user.username || user.email}</strong>.
                </p>

                <label className="block">
                    <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">Reason</span>
                    <textarea
                        data-testid="admin-users-mod-reason"
                        value={reason}
                        onChange={(e) => { setReason(e.target.value); if (error) setError(null); }}
                        rows={3}
                        placeholder={type === "messaging" ? "Why is messaging being suspended?" : "Why are you taking this action?"}
                        className="w-full px-3 py-2 rounded-xl bg-secondary border border-border focus:border-primary focus:bg-background outline-none text-[13.5px] resize-y"
                    />
                </label>

                {type === "suspend" && (
                    <label className="block mt-3">
                        <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Duration in days (leave blank for indefinite)
                        </span>
                        <input
                            data-testid="admin-users-mod-days"
                            type="number"
                            min="1"
                            value={days}
                            onChange={(e) => { setDays(e.target.value); if (error) setError(null); }}
                            placeholder="e.g. 7"
                            className="w-full h-11 px-3.5 rounded-xl bg-secondary border border-border focus:border-primary focus:bg-background outline-none text-[13.5px]"
                        />
                    </label>
                )}

                {error && (
                    <p data-testid="admin-users-mod-error" className="mt-3 text-[12px] text-destructive font-medium">
                        {error}
                    </p>
                )}

                <div className="mt-4 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="admin-users-mod-cancel"
                        className="flex-1 h-11 rounded-xl border border-border bg-secondary text-[13px] font-medium hover:bg-muted"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={apply}
                        data-testid="admin-users-mod-apply"
                        className={[
                            "flex-1 h-11 rounded-xl text-[13px] font-medium text-white",
                            meta.tone === "destructive"
                                ? "bg-destructive"
                                : "bg-amber-500 dark:bg-amber-600",
                        ].join(" ")}
                    >
                        {meta.cta}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminUsers;
